// src/data/integrationHealthClient.js
//
// Slice U6 — read-only client for the two Data Health endpoints landed
// on the server in the same slice:
//
//   GET /api/integrations/health
//   GET /api/sync-events
//
// Same shape as src/data/leasesPortfolioClient.js: typed errors, no
// audit (reads), 60s client cache via integrationHealthCache.js, and
// the cache is full-busted by every portfolio write through cacheBus.
//
// Per CONTRACT.md ("Integration health & sync events"):
//   - viewer-readable
//   - no audit emission (reads)
//   - server returns camelCase rows
//   - errors are typed instances from src/data/errors.js

import {
  AuthError,
  NetworkError,
  errorFromResponse,
} from "./errors.js";
import {
  KEY_INTEGRATIONS_HEALTH,
  KEY_PREFIX_SYNC_EVENTS,
  get as cacheGet,
  set as cacheSet,
  keyFor,
} from "../services/integrationHealthCache.js";
import { log } from "../utils/logger.js";

async function jsonRequest(url) {
  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    log.error("integrationHealthClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("integrationHealthClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * @typedef  {Object} ConnectionHealth
 * @property {'workday'|'envoy'|'verkada'|'spacebase'} sourceId
 * @property {string} displayName
 * @property {string} type
 * @property {number} cadenceHours
 * @property {string|null} lastSuccessAt
 * @property {string|null} lastAttemptAt
 * @property {number|null} lastRecordCount
 * @property {number}      consecutiveFailures
 * @property {number}      successRate
 * @property {number}      attemptsConsidered
 * @property {number}      healthScore
 * @property {'green'|'yellow'|'red'|'pending'} healthStatus
 * @property {string|null} nextSyncDue
 */

/** @returns {Promise<ConnectionHealth[]>} */
export async function listIntegrationHealth() {
  const cached = cacheGet(KEY_INTEGRATIONS_HEALTH);
  if (cached) {
    log.api("integrationHealthClient.listIntegrationHealth cache hit", { count: cached.length });
    return cached;
  }
  const rows = await jsonRequest("/api/integrations/health");
  cacheSet(KEY_INTEGRATIONS_HEALTH, rows);
  log.api("integrationHealthClient.listIntegrationHealth fetched", { count: rows.length });
  return rows;
}

/**
 * @typedef  {Object} SyncEvent
 * @property {string}  eventId
 * @property {string}  sourceId
 * @property {string}  eventType
 * @property {string}  triggeredBy
 * @property {string}  startedAt
 * @property {string|null} completedAt
 * @property {number|null} durationMs
 * @property {number|null} recordsFetched
 * @property {number|null} recordsAccepted
 * @property {number|null} recordsRejected
 * @property {number|null} httpStatusCode
 * @property {string|null} errorCode
 * @property {string|null} errorMessage
 * @property {string|null} healthStatusAfter
 * @property {string|null} createdAt
 */

/**
 * @param {{ sourceId?: string, since?: string, limit?: number }} [filters]
 * @returns {Promise<SyncEvent[]>}
 */
export async function listSyncEvents(filters = {}) {
  const params = new URLSearchParams();
  // Server reads `source_id` (snake), not `sourceId` — we honor the
  // wire contract here so callers can stay in camelCase.
  if (filters.sourceId) params.set("source_id", filters.sourceId);
  if (filters.since)    params.set("since",     filters.since);
  if (filters.limit)    params.set("limit",     String(filters.limit));

  const key = keyFor(KEY_PREFIX_SYNC_EVENTS, {
    sourceId: filters.sourceId,
    since:    filters.since,
    limit:    filters.limit,
  });
  const cached = cacheGet(key);
  if (cached) {
    log.api("integrationHealthClient.listSyncEvents cache hit", { count: cached.length });
    return cached;
  }

  const qs = params.toString();
  const url = qs ? `/api/sync-events?${qs}` : "/api/sync-events";
  const rows = await jsonRequest(url);
  cacheSet(key, rows);
  log.api("integrationHealthClient.listSyncEvents fetched", { count: rows.length });
  return rows;
}

export { AuthError, NetworkError } from "./errors.js";
